import { useSEO } from '../../../utils/seo';

export default function LicensedInsuredContractorIdaho() {
  useSEO({
    title: 'Why Hire a Licensed and Insured Contractor in Idaho | Legal Requirements',
    description: 'Learn why hiring licensed and insured contractors in Idaho is essential. Understand legal requirements, protection benefits, and how to verify credentials in Boise and Treasure Valley.',
    keywords: 'licensed contractor Idaho, insured contractor Boise, Idaho contractor requirements, RCE license Idaho, contractor insurance Treasure Valley',
    canonical: `${import.meta.env.VITE_SITE_URL}/blog/licensed-insured-contractor-idaho`,
    ogType: 'article',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: 'Why Hire a Licensed and Insured Contractor in Idaho | Legal Requirements',
          description: 'Learn why hiring licensed and insured contractors in Idaho is essential. Understand legal requirements, protection benefits, and how to verify credentials in Boise and Treasure Valley.',
          author: {
            '@type': 'Organization',
            name: 'Fix Right Services'
          },
          publisher: {
            '@type': 'Organization',
            name: 'Fix Right Services',
            logo: {
              '@type': 'ImageObject',
              url: `${import.meta.env.VITE_SITE_URL}/images/logo.svg`
            }
          },
          datePublished: '2025-01-15',
          dateModified: '2025-01-15'
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: import.meta.env.VITE_SITE_URL
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: `${import.meta.env.VITE_SITE_URL}/blog`
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: 'Why Hire a Licensed and Insured Contractor in Idaho',
              item: `${import.meta.env.VITE_SITE_URL}/blog/licensed-insured-contractor-idaho`
            }
          ]
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What does it mean for a contractor to be licensed in Idaho?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'A licensed Idaho contractor has passed state exams, met experience requirements, and maintains active registration with the Idaho Bureau of Occupational Licenses. License types include RCE (Residential Contractor), RCT (Residential Tradesman), and specialty licenses. Licensed contractors must follow building codes and can legally pull permits.'
              }
            },
            {
              '@type': 'Question',
              name: 'Is it illegal to hire an unlicensed contractor in Idaho?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'While it\'s not illegal for homeowners to hire unlicensed contractors, it\'s risky and not recommended. Idaho law requires contractors to be licensed for most home improvement work over $2,000. Unlicensed contractors cannot pull permits, and you have no legal recourse if problems arise.'
              }
            },
            {
              '@type': 'Question',
              name: 'What insurance should a Boise contractor have?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Idaho contractors should carry: General Liability Insurance (minimum $300,000 recommended), Workers\' Compensation Insurance (if they have employees), and Commercial Auto Insurance (for work vehicles). These protect you from property damage, injuries, and legal liability.'
              }
            },
            {
              '@type': 'Question',
              name: 'What happens if I hire an uninsured contractor and someone gets hurt?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'You could be held personally liable for medical bills, lost wages, and legal fees. Without workers\' comp insurance, injured workers can sue homeowners directly. This can cost $50,000-$500,000+ depending on injury severity. Always verify insurance before hiring.'
              }
            },
            {
              '@type': 'Question',
              name: 'How do I verify a contractor is licensed and insured in Idaho?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'For licenses: Visit ibol.idaho.gov or call (208) 334-3233. For insurance: Request a Certificate of Insurance (COI) and call the insurance company to verify coverage is active. Check that coverage amounts are adequate and you\'re listed as additional insured.'
              }
            },
            {
              '@type': 'Question',
              name: 'Can licensed contractors charge more than unlicensed ones?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, licensed and insured contractors typically charge 10-20% more due to licensing fees, insurance costs, and overhead. However, this protects you from liability, ensures quality work, and provides legal recourse. The small premium is worth the protection and peace of mind.'
              }
            }
          ]
        }
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <article className="max-w-4xl mx-auto px-6 py-16">
        <header className="mb-12">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
            <a href="/" className="hover:text-teal-600 transition-colors">Home</a>
            <span>/</span>
            <a href="/blog" className="hover:text-teal-600 transition-colors">Blog</a>
            <span>/</span>
            <span className="text-gray-900">Licensed and Insured Contractors</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Why Hire a Licensed and Insured Contractor in Idaho | Legal Requirements
          </h1>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <time dateTime="2025-01-15">January 15, 2025</time>
            <span>•</span>
            <span>10 min read</span>
          </div>
        </header>

        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            Hiring a <strong>licensed and insured contractor in Idaho</strong> isn't just a good idea—it's essential protection for your home and finances. Whether you're planning a <strong>fence installation in Boise</strong>, <strong>kitchen remodel</strong>, or <strong>handyman repairs</strong>, understanding Idaho's contractor licensing and insurance requirements helps you avoid costly mistakes and legal liability. This guide explains what these credentials mean and why they matter for <strong>Treasure Valley homeowners</strong>.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What Does "Licensed" Mean in Idaho?</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Idaho requires contractors to obtain licenses through the Idaho Bureau of Occupational Licenses (IBOL). A license proves the contractor has:
          </p>
          <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
            <li><strong>Passed state examinations</strong> demonstrating knowledge of building codes, safety, and construction practices</li>
            <li><strong>Met experience requirements</strong> (typically 2-4 years in the trade)</li>
            <li><strong>Provided proof of insurance</strong> and financial responsibility</li>
            <li><strong>Paid licensing fees</strong> and maintains active registration</li>
            <li><strong>Agreed to follow state regulations</strong> and building codes</li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Idaho Contractor License Types</h3>
          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">License Type</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Code</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Scope of Work</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Residential Contractor</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">RCE</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">General residential construction, remodeling, additions</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Residential Tradesman</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">RCT</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Specific trades (plumbing, electrical, HVAC)</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Public Works Contractor</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">PWC</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Commercial and public projects</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Specialty Contractor</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Various</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Specific specialties (roofing, siding, fencing, etc.)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Why Licensing Matters for Homeowners</h2>
          <div className="space-y-6 mb-8">
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">1. Legal Compliance</h3>
              <p className="text-gray-700">Idaho law requires contractors to be licensed for most home improvement work over $2,000. Unlicensed contractors operating illegally cannot pull permits or legally perform many types of work.</p>
            </div>

            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">2. Quality Assurance</h3>
              <p className="text-gray-700">Licensed contractors have demonstrated competency through exams and experience requirements. They understand building codes, safety standards, and proper construction techniques.</p>
            </div>

            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">3. Legal Recourse</h3>
              <p className="text-gray-700">If problems arise, you can file complaints with IBOL. The state can investigate, mediate disputes, and take disciplinary action including license suspension or revocation.</p>
            </div>

            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">4. Permit Authority</h3>
              <p className="text-gray-700">Only licensed contractors can pull building permits in most Idaho jurisdictions. Permits ensure work meets code and passes inspections, protecting your home's value and safety.</p>
            </div>

            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">5. Professionalism Standards</h3>
              <p className="text-gray-700">Licensed contractors must maintain their credentials, stay current with code changes, and operate legitimate businesses. This indicates commitment to their profession.</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What Does "Insured" Mean?</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Insurance protects both the contractor and homeowner from financial loss due to accidents, injuries, or property damage. Idaho contractors should carry multiple types of insurance:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Required Insurance Types</h3>
          <div className="space-y-6 mb-8">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-3">General Liability Insurance</h4>
              <p className="text-gray-700 mb-2"><strong>Minimum recommended:</strong> $300,000 coverage (many carry $1-2 million)</p>
              <p className="text-gray-700 mb-3"><strong>Covers:</strong></p>
              <ul className="space-y-1 text-gray-700">
                <li>• Property damage to your home during work</li>
                <li>• Bodily injury to third parties</li>
                <li>• Legal defense costs if sued</li>
                <li>• Damage to neighboring properties</li>
              </ul>
              <p className="text-gray-700 mt-3"><strong>Example:</strong> Contractor accidentally breaks a window or damages flooring—insurance pays for repairs.</p>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Workers' Compensation Insurance</h4>
              <p className="text-gray-700 mb-2"><strong>Required if:</strong> Contractor has employees</p>
              <p className="text-gray-700 mb-3"><strong>Covers:</strong></p>
              <ul className="space-y-1 text-gray-700">
                <li>• Medical expenses for injured workers</li>
                <li>• Lost wages during recovery</li>
                <li>• Disability benefits if applicable</li>
                <li>• Legal protection for homeowner</li>
              </ul>
              <p className="text-gray-700 mt-3"><strong>Example:</strong> Worker falls from ladder and breaks arm—workers' comp pays medical bills and wages. Without it, you could be sued.</p>
            </div>

            <div className="bg-orange-50 rounded-lg p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Commercial Auto Insurance</h4>
              <p className="text-gray-700 mb-2"><strong>Required if:</strong> Contractor uses vehicles for work</p>
              <p className="text-gray-700 mb-3"><strong>Covers:</strong></p>
              <ul className="space-y-1 text-gray-700">
                <li>• Vehicle accidents during project</li>
                <li>• Damage to your property from work vehicles</li>
                <li>• Injuries from vehicle-related incidents</li>
              </ul>
              <p className="text-gray-700 mt-3"><strong>Example:</strong> Contractor's truck backs into your garage—commercial auto pays for repairs.</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Risks of Hiring Unlicensed/Uninsured Contractors</h2>
          <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-4">What Can Go Wrong:</h3>
            <div className="space-y-4 text-gray-700">
              <div>
                <p className="font-semibold mb-1">💰 Financial Liability</p>
                <p>If an uninsured worker is injured on your property, you could be sued for medical bills ($50,000-$500,000+), lost wages, and pain and suffering.</p>
              </div>
              <div>
                <p className="font-semibold mb-1">🏚️ Property Damage</p>
                <p>Without liability insurance, you pay out-of-pocket for any damage the contractor causes to your home or belongings.</p>
              </div>
              <div>
                <p className="font-semibold mb-1">⚖️ No Legal Recourse</p>
                <p>Unlicensed contractors aren't registered with the state. If they do poor work or disappear, you have limited legal options.</p>
              </div>
              <div>
                <p className="font-semibold mb-1">🚫 Permit Problems</p>
                <p>Unlicensed contractors can't pull permits. Unpermitted work can: prevent home sales, void insurance claims, result in fines, and require expensive removal/redo.</p>
              </div>
              <div>
                <p className="font-semibold mb-1">📉 Decreased Home Value</p>
                <p>Unpermitted or substandard work discovered during home inspections can kill sales or force price reductions of $10,000-$50,000+.</p>
              </div>
              <div>
                <p className="font-semibold mb-1">🔨 Poor Quality Work</p>
                <p>Unlicensed contractors may lack proper training, leading to code violations, safety hazards, and expensive repairs (often 2-3x the original project cost).</p>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Real-World Cost Comparison</h2>
          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Scenario</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Licensed/Insured</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Unlicensed/Uninsured</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Worker injury on site</td>
                  <td className="border border-gray-300 px-4 py-3 text-green-600 font-semibold">$0 (insurance covers)</td>
                  <td className="border border-gray-300 px-4 py-3 text-red-600 font-semibold">$50,000-$500,000+ (you pay)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Contractor damages your home</td>
                  <td className="border border-gray-300 px-4 py-3 text-green-600 font-semibold">$0 (insurance covers)</td>
                  <td className="border border-gray-300 px-4 py-3 text-red-600 font-semibold">$5,000-$50,000+ (you pay)</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Poor work needs redoing</td>
                  <td className="border border-gray-300 px-4 py-3 text-green-600 font-semibold">File complaint, legal recourse</td>
                  <td className="border border-gray-300 px-4 py-3 text-red-600 font-semibold">Limited options, likely total loss</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Unpermitted work discovered</td>
                  <td className="border border-gray-300 px-4 py-3 text-green-600 font-semibold">Properly permitted, no issue</td>
                  <td className="border border-gray-300 px-4 py-3 text-red-600 font-semibold">$5,000-$30,000 to fix/permit</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Project cost premium</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">10-20% more upfront</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Cheaper initially, but...</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 mb-8">
            <p className="text-gray-700 font-semibold">
              <strong>Bottom Line:</strong> Saving 10-20% by hiring unlicensed/uninsured contractors can cost you 10-100x more if something goes wrong. The small premium for licensed and insured contractors is the best insurance you can buy.
            </p>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">How to Verify Credentials</h2>
          
          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Verify Idaho License</h3>
          <ol className="list-decimal pl-6 mb-8 space-y-2 text-gray-700">
            <li><strong>Visit IBOL website:</strong> Go to <a href="https://ibol.idaho.gov" className="text-teal-600 hover:underline" target="_blank" rel="noopener noreferrer">ibol.idaho.gov</a></li>
            <li><strong>Search by name or license number:</strong> Use the contractor lookup tool</li>
            <li><strong>Verify status is "Active":</strong> Check expiration date</li>
            <li><strong>Check for disciplinary actions:</strong> Review complaint history</li>
            <li><strong>Confirm license type matches your project:</strong> RCE, RCT, specialty, etc.</li>
            <li><strong>Call to verify:</strong> (208) 334-3233 for phone confirmation</li>
          </ol>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Verify Insurance</h3>
          <ol className="list-decimal pl-6 mb-8 space-y-2 text-gray-700">
            <li><strong>Request Certificate of Insurance (COI):</strong> Ask contractor to provide current COI</li>
            <li><strong>Check coverage types:</strong> Verify general liability and workers' comp are listed</li>
            <li><strong>Verify coverage amounts:</strong> General liability should be $300,000+ minimum</li>
            <li><strong>Call insurance company directly:</strong> Verify policy is active and in good standing</li>
            <li><strong>Check expiration dates:</strong> Coverage must extend through project completion</li>
            <li><strong>Confirm you're listed as additional insured:</strong> Should be added for project duration</li>
            <li><strong>Never accept photocopies or expired certificates:</strong> Always verify with insurance company</li>
          </ol>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Red Flags: Walk Away If...</h2>
          <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-8">
            <ul className="space-y-2 text-gray-700">
              <li>🚩 <strong>Can't provide license number</strong> or says "I don't need one"</li>
              <li>🚩 <strong>Won't provide insurance certificates</strong> or says "I'm covered under someone else"</li>
              <li>🚩 <strong>Offers cash discount</strong> to avoid paper trail</li>
              <li>🚩 <strong>Says permits aren't necessary</strong> when they clearly are</li>
              <li>🚩 <strong>Pressure tactics</strong> to sign immediately</li>
              <li>🚩 <strong>Significantly lower bid</strong> than licensed competitors (20%+ below)</li>
              <li>🚩 <strong>No physical business address</strong> or only P.O. box</li>
              <li>🚩 <strong>Won't provide written contract</strong> or detailed estimate</li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Common Myths Debunked</h2>
          <div className="space-y-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-bold text-gray-900 mb-2">Myth: "Small jobs don't need licensed contractors"</h4>
              <p className="text-gray-700"><strong>Truth:</strong> Idaho law requires licensing for most work over $2,000. Even small jobs can cause big problems if done wrong. Always hire licensed contractors regardless of project size.</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-bold text-gray-900 mb-2">Myth: "My homeowner's insurance covers contractor injuries"</h4>
              <p className="text-gray-700"><strong>Truth:</strong> Most homeowner policies exclude coverage for contractor injuries. If the contractor doesn't have workers' comp, you're personally liable. Always verify insurance.</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-bold text-gray-900 mb-2">Myth: "Licensed contractors are too expensive"</h4>
              <p className="text-gray-700"><strong>Truth:</strong> Licensed contractors charge 10-20% more, but this protects you from potentially catastrophic costs. One injury or major mistake by an unlicensed contractor can cost 10-100x more than the savings.</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-bold text-gray-900 mb-2">Myth: "I can just get a permit myself"</h4>
              <p className="text-gray-700"><strong>Truth:</strong> Homeowners can pull permits for their own work, but most jurisdictions require licensed contractors for work they perform. DIY permits don't protect you from unlicensed contractor liability.</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Questions to Ask Before Hiring</h2>
          <div className="bg-teal-50 rounded-lg p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-4">Essential Questions:</h3>
            <ol className="space-y-3 text-gray-700">
              <li><strong>1. What is your Idaho contractor license number?</strong> (Verify at ibol.idaho.gov)</li>
              <li><strong>2. Can you provide current certificates of insurance?</strong> (General liability and workers' comp)</li>
              <li><strong>3. Will you pull all necessary permits?</strong> (Should be yes for permitted work)</li>
              <li><strong>4. Who will actually be doing the work?</strong> (Ensure workers are also licensed/insured)</li>
              <li><strong>5. Can you provide references from recent similar projects?</strong> (Contact at least 3)</li>
              <li><strong>6. What warranties do you offer?</strong> (Get in writing)</li>
              <li><strong>7. How long have you been licensed in Idaho?</strong> (3+ years preferred)</li>
            </ol>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6 mb-12">
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">What does it mean for a contractor to be licensed in Idaho?</h3>
              <p className="text-gray-700">A licensed Idaho contractor has passed state exams, met experience requirements, and maintains active registration with the Idaho Bureau of Occupational Licenses. License types include RCE (Residential Contractor), RCT (Residential Tradesman), and specialty licenses. Licensed contractors must follow building codes and can legally pull permits.</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Is it illegal to hire an unlicensed contractor in Idaho?</h3>
              <p className="text-gray-700">While it's not illegal for homeowners to hire unlicensed contractors, it's risky and not recommended. Idaho law requires contractors to be licensed for most home improvement work over $2,000. Unlicensed contractors cannot pull permits, and you have no legal recourse if problems arise.</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">What insurance should a Boise contractor have?</h3>
              <p className="text-gray-700">Idaho contractors should carry: General Liability Insurance (minimum $300,000 recommended), Workers' Compensation Insurance (if they have employees), and Commercial Auto Insurance (for work vehicles). These protect you from property damage, injuries, and legal liability.</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">What happens if I hire an uninsured contractor and someone gets hurt?</h3>
              <p className="text-gray-700">You could be held personally liable for medical bills, lost wages, and legal fees. Without workers' comp insurance, injured workers can sue homeowners directly. This can cost $50,000-$500,000+ depending on injury severity. Always verify insurance before hiring.</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">How do I verify a contractor is licensed and insured in Idaho?</h3>
              <p className="text-gray-700">For licenses: Visit ibol.idaho.gov or call (208) 334-3233. For insurance: Request a Certificate of Insurance (COI) and call the insurance company to verify coverage is active. Check that coverage amounts are adequate and you're listed as additional insured.</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Can licensed contractors charge more than unlicensed ones?</h3>
              <p className="text-gray-700">Yes, licensed and insured contractors typically charge 10-20% more due to licensing fees, insurance costs, and overhead. However, this protects you from liability, ensures quality work, and provides legal recourse. The small premium is worth the protection and peace of mind.</p>
            </div>
          </div>

          <div className="bg-teal-600 text-white rounded-lg p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Work With Confidence</h2>
            <p className="text-xl mb-6">
              Fix Right Services is fully licensed (RCE #12345) and insured with $2 million liability coverage. We provide all credentials upfront and pull all necessary permits.
            </p>
            <a 
              href="/#contact" 
              className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Request Free Estimate
            </a>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Related Articles:</h3>
            <ul className="space-y-2">
              <li>
                <a href="/blog/how-to-vet-contractor-idaho" className="text-teal-600 hover:underline">
                  How to Vet a Contractor in Idaho
                </a>
              </li>
              <li>
                <a href="/blog/how-to-choose-remodeling-contractor-boise" className="text-teal-600 hover:underline">
                  How to Choose a Remodeling Contractor in Boise
                </a>
              </li>
              <li>
                <a href="/blog/handyman-vs-contractor-idaho" className="text-teal-600 hover:underline">
                  Handyman vs. Contractor: Which Do You Need in Idaho?
                </a>
              </li>
            </ul>
          </div>
        </div>
      </article>
    </div>
  );
}